import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useState, useEffect } from "react";
import { useMutation, useQuery } from "convex/react";
import { api } from "../../../../../convex/_generated/api";
import { useToast } from "@/components/ui/use-toast";
import { Id } from "../../../../../convex/_generated/dataModel";

interface CreateBidSheetProps {
  isOpen: boolean;
  onClose: () => void;
  initialClient?: string;
}

export function CreateBidSheet({ isOpen, onClose, initialClient = "" }: CreateBidSheetProps) {
  const createBid = useMutation(api.bids.create);
  const updateBid = useMutation(api.bids.update);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();
  const [selectedSource, setSelectedSource] = useState<"lead" | "client">("lead");
  const [selectedCompanyId, setSelectedCompanyId] = useState<string>("");

  // Fetch leads and clients from the businesses table
  const leads = useQuery(api.businesses.getLeads) || [];
  const clients = useQuery(api.businesses.getClients) || [];

  // Determine if fields should be disabled based on initialClient
  const isPrePopulated = Boolean(initialClient);

  const [formData, setFormData] = useState({
    title: "",
    clientCompany: "",
    description: "",
    status: "Draft" as const,
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { id, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [id]: value,
    }));
  };

  const handleSelectChange = (name: string, value: string) => {
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Track if we've already processed the initialClient to avoid infinite loops
  const [hasProcessedInitialClient, setHasProcessedInitialClient] = useState(false);

  // Update form when initialClient changes
  useEffect(() => {
    if (initialClient && !hasProcessedInitialClient && leads.length > 0 && clients.length > 0) {
      // Try to find the client in leads first, then clients
      const matchingLead = leads.find((lead: any) => lead.name === initialClient);
      const matchingClient = clients.find((client: any) => client.name === initialClient);

      if (matchingLead) {
        setSelectedSource("lead");
        setSelectedCompanyId(matchingLead._id);
        setFormData((prev) => ({
          ...prev,
          clientCompany: matchingLead.name,
        }));
      } else if (matchingClient) {
        setSelectedSource("client");
        setSelectedCompanyId(matchingClient._id);
        setFormData((prev) => ({
          ...prev,
          clientCompany: matchingClient.name,
        }));
      } else {
        // If no match found, just set the company name
        setFormData((prev) => ({
          ...prev,
          clientCompany: initialClient,
        }));
      }
      setHasProcessedInitialClient(true);
    }
  }, [initialClient, leads, clients, hasProcessedInitialClient]);

  // Update form when a company is manually selected
  useEffect(() => {
    if (selectedSource && selectedCompanyId) {
      if (selectedSource === "lead") {
        const lead = leads.find((lead: any) => lead._id === selectedCompanyId);
        if (lead) {
          setFormData((prev) => ({
            ...prev,
            clientCompany: lead.name,
          }));
        }
      } else if (selectedSource === "client") {
        const client = clients.find((client: any) => client._id === selectedCompanyId);
        if (client) {
          setFormData((prev) => ({
            ...prev,
            clientCompany: client.name,
          }));
        }
      }
    }
  }, [selectedSource, selectedCompanyId, leads, clients]);

  // Reset state when sheet is closed
  useEffect(() => {
    if (!isOpen) {
      setHasProcessedInitialClient(false);
      setSelectedSource("lead");
      setSelectedCompanyId("");
      setFormData({
        title: "",
        status: "Draft",
        description: "",
        clientCompany: "",
      });
    }
  }, [isOpen]);

  const validateForm = () => {
    // Validate required fields
    if (!formData.title.trim()) {
      toast({
        title: "Validation Error",
        description: "Bid title is required",
        variant: "destructive",
      });
      return false;
    }

    if (!selectedCompanyId) {
      toast({
        title: "Validation Error",
        description: "Please select a lead or client",
        variant: "destructive",
      });
      return false;
    }

    if (!formData.clientCompany.trim()) {
      toast({
        title: "Validation Error",
        description: "Client company is required",
        variant: "destructive",
      });
      return false;
    }

    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    try {
      setIsSubmitting(true);

      // First, create the bid with the required fields
      const bidId = await createBid({
        title: formData.title,
        status: formData.status,
      });

      // Then, update the bid with the additional fields
      await updateBid({
        id: bidId as Id<"bids">,
        title: formData.title,
        status: formData.status,
        description: formData.description || undefined,
        businessId: selectedCompanyId ? (selectedCompanyId as Id<"businesses">) : undefined,
        client: {
          company: formData.clientCompany,
          contactName: "",
          email: "",
          phone: "",
          address: undefined,
          title: undefined,
        },
      });

      toast({
        title: "Success!",
        description: "Bid created successfully",
      });
      onClose();
    } catch (error: any) {
      toast({
        title: "Error",
        description: "Failed to create bid",
        variant: "destructive",
      });
      console.error(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Sheet open={isOpen} onOpenChange={onClose}>
        <SheetContent className="w-[500px] sm:max-w-[500px]">
          <SheetHeader>
            <SheetTitle>Create New Bid</SheetTitle>
            <SheetDescription>Fill in the details to create a new bid proposal.</SheetDescription>
          </SheetHeader>
          <ScrollArea className="h-[calc(100vh-180px)] pr-4">
            <div className="space-y-6 px-2 py-6">
              <div className="space-y-2">
                <Label htmlFor="title">Bid Title</Label>
                <Input
                  id="title"
                  placeholder="Enter bid title"
                  value={formData.title}
                  onChange={handleInputChange}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  placeholder="Enter bid description"
                  rows={4}
                  value={formData.description}
                  onChange={handleInputChange}
                />
              </div>

              <div className="space-y-6 border-t border-gray-200 py-4">
                <h3 className="text-lg font-medium">Client Information</h3>

                <div className="space-y-2">
                  <Label>Company Source</Label>
                  <Select
                    value={selectedSource}
                    onValueChange={(value: "lead" | "client") => {
                      setSelectedSource(value);
                      setSelectedCompanyId("");
                    }}
                    disabled={isPrePopulated}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select source" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="lead">Select from Leads</SelectItem>
                      <SelectItem value="client">Select from Clients</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {selectedSource === "lead" && (
                  <div className="space-y-2">
                    <Label htmlFor="lead-select">Select Lead</Label>
                    <Select value={selectedCompanyId} onValueChange={setSelectedCompanyId}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select a lead" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectGroup>
                          <SelectLabel>Leads</SelectLabel>
                          {leads.length === 0 ? (
                            <SelectItem value="no-leads" disabled>
                              No leads available
                            </SelectItem>
                          ) : (
                            leads.map((lead: any) => (
                              <SelectItem key={lead._id} value={lead._id}>
                                {lead.name}
                              </SelectItem>
                            ))
                          )}
                        </SelectGroup>
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {selectedSource === "client" && (
                  <div className="space-y-2">
                    <Label htmlFor="client-select">Select Client</Label>
                    <Select value={selectedCompanyId} onValueChange={setSelectedCompanyId}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select a client" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectGroup>
                          <SelectLabel>Clients</SelectLabel>
                          {clients.length === 0 ? (
                            <SelectItem value="no-clients" disabled>
                              No clients available
                            </SelectItem>
                          ) : (
                            clients.map((client: any) => (
                              <SelectItem key={client._id} value={client._id}>
                                {client.name}
                              </SelectItem>
                            ))
                          )}
                        </SelectGroup>
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>

              <Button className="w-full" onClick={handleSubmit} disabled={isSubmitting}>
                {isSubmitting ? "Creating..." : "Create Bid"}
              </Button>
            </div>
          </ScrollArea>
        </SheetContent>
      </Sheet>
    </>
  );
}
